import {
  VisitorSessionEvent,
  VisitorSessionListItem,
  VisitorSessionSourceSummary,
} from '../models/visitor-session';
import {
  formatEventTimestamp,
  formatIdentity,
  formatLanding,
  formatSourceSummary,
} from './visitor-session-display';

export interface VisitorSessionExportSource {
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
}

export interface VisitorSessionExportEvent {
  eventId: string;
  occurredAt: string;
  type: string;
  route: string;
  landingPage: string;
  source: string | VisitorSessionExportSource;
  metadata?: Record<string, unknown>;
}

export interface VisitorSessionExportSession {
  sessionId: string;
  visitorId: string;
  identity: 'Identified' | 'Anonymous';
  userId: string;
  createdAt: string;
  lastActivityAt: string;
  landingKey: string;
  landingPath: string;
  landing: string;
  source: string | VisitorSessionExportSource;
  language: string;
  device: string;
  eventCount: string | number;
  lastEventType: string;
}

export interface VisitorSessionExportPayload {
  session: VisitorSessionExportSession;
  events: VisitorSessionExportEvent[];
}

function hasText(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function exportScalar(value: unknown): string {
  if (value === null || value === undefined) {
    return '-';
  }
  if (typeof value === 'string' && value.trim() === '') {
    return '-';
  }
  return String(value);
}

function cleanRecord(
  record: Record<string, unknown> | null | undefined
): Record<string, unknown> | undefined {
  if (!record || typeof record !== 'object' || Array.isArray(record)) {
    return undefined;
  }
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (value === null || value === undefined) {
      continue;
    }
    if (typeof value === 'string' && value.trim() === '') {
      continue;
    }
    out[key] = value;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function normalizeSourceForExport(
  source?: VisitorSessionSourceSummary | string | null
): string | VisitorSessionExportSource {
  if (!source) {
    return '-';
  }
  if (typeof source === 'string') {
    return source.trim() || '-';
  }

  const normalized: VisitorSessionExportSource = {};
  const fields: Array<keyof VisitorSessionExportSource> = [
    'referrer',
    'utmSource',
    'utmMedium',
    'utmCampaign',
    'utmTerm',
    'utmContent',
  ];

  for (const field of fields) {
    const value = source[field];
    if (hasText(value)) {
      normalized[field] = String(value).trim();
    }
  }

  return Object.keys(normalized).length > 0 ? normalized : '-';
}

function resolveEventRoute(event: VisitorSessionEvent): string {
  const nested = event.pageContext as { routePath?: string } | undefined;
  return exportScalar(event.routePath || event.path || nested?.routePath);
}

function resolveEventLanding(event: VisitorSessionEvent): string {
  const nested = event.pageContext as
    | { landingPath?: string; landingKey?: string; surface?: string }
    | undefined;
  const path = (event.landingPath ?? nested?.landingPath)?.trim();
  const key = (event.landingKey ?? nested?.landingKey)?.trim();
  const page = (event.page ?? nested?.surface)?.trim();

  if (path && key) {
    return `${path} (${key})`;
  }
  return exportScalar(path || key || page);
}

function resolveEventSourceText(event: VisitorSessionEvent): string {
  const source =
    event.sourceSummary ??
    (event.source as VisitorSessionListItem['sourceSummary']);
  if (!source) {
    return '-';
  }
  if (typeof source === 'string') {
    return exportScalar(source);
  }
  return formatSourceSummary({ sourceSummary: source });
}

function resolveEventMetadata(
  event: VisitorSessionEvent
): Record<string, unknown> | undefined {
  const raw =
    event.metadata ?? event.payload ?? event.actionDetails ?? event.action;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed || trimmed === '-') {
      return undefined;
    }
    try {
      const parsed = JSON.parse(trimmed);
      return cleanRecord(parsed as Record<string, unknown>);
    } catch {
      return { value: trimmed };
    }
  }
  return cleanRecord(raw as Record<string, unknown>);
}

export function normalizeEventForExport(
  event: VisitorSessionEvent
): VisitorSessionExportEvent {
  const metadata = resolveEventMetadata(event);
  const normalized: VisitorSessionExportEvent = {
    eventId: exportScalar(event.eventId),
    occurredAt: exportScalar(formatEventTimestamp(event)),
    type: exportScalar(event.eventType || event.type),
    route: resolveEventRoute(event),
    landingPage: resolveEventLanding(event),
    source: normalizeSourceForExport(
      event.sourceSummary ??
        (event.source as VisitorSessionListItem['sourceSummary'])
    ),
  };

  if (metadata) {
    normalized.metadata = metadata;
  }

  return normalized;
}

export function normalizeSessionForExport(
  session: VisitorSessionListItem
): VisitorSessionExportSession {
  const source =
    session.sourceSummary ?? session.firstTouchSource ?? null;

  return {
    sessionId: exportScalar(session.sessionId),
    visitorId: exportScalar(session.visitorId),
    identity: formatIdentity(session.identified),
    userId: exportScalar(session.userId),
    createdAt: exportScalar(session.createdAt),
    lastActivityAt: exportScalar(session.lastActivityAt),
    landingKey: exportScalar(session.landingKey),
    landingPath: exportScalar(session.landingPath),
    landing: formatLanding(session),
    source: normalizeSourceForExport(source),
    language: exportScalar(session.language),
    device: exportScalar(session.deviceType),
    eventCount:
      session.eventCount === null || session.eventCount === undefined
        ? '-'
        : session.eventCount,
    lastEventType: exportScalar(session.lastEventType),
  };
}

function sortEventsByTime(events: VisitorSessionEvent[]): VisitorSessionEvent[] {
  return [...events].sort((a, b) => {
    const aTime = formatEventTimestamp(a) || '';
    const bTime = formatEventTimestamp(b) || '';
    return aTime.localeCompare(bTime);
  });
}

export function buildSessionExportPayload(
  session: VisitorSessionListItem,
  events: VisitorSessionEvent[]
): VisitorSessionExportPayload {
  return {
    session: normalizeSessionForExport(session),
    events: sortEventsByTime(events).map(normalizeEventForExport),
  };
}

export function buildSessionExportJson(
  session: VisitorSessionListItem,
  events: VisitorSessionEvent[]
): string {
  return JSON.stringify(buildSessionExportPayload(session, events), null, 2);
}

function formatSourceForPrompt(
  source: string | VisitorSessionExportSource
): string {
  if (typeof source === 'string') {
    return source;
  }
  const parts: string[] = [];
  if (source.referrer) {
    parts.push(`referrer: ${source.referrer}`);
  }
  if (source.utmSource) {
    parts.push(`utm_source: ${source.utmSource}`);
  }
  if (source.utmMedium) {
    parts.push(`utm_medium: ${source.utmMedium}`);
  }
  if (source.utmCampaign) {
    parts.push(`utm_campaign: ${source.utmCampaign}`);
  }
  if (source.utmTerm) {
    parts.push(`utm_term: ${source.utmTerm}`);
  }
  if (source.utmContent) {
    parts.push(`utm_content: ${source.utmContent}`);
  }
  return parts.length > 0 ? parts.join('\n') : '-';
}

function formatEventTimelineForPrompt(events: VisitorSessionExportEvent[]): string {
  if (events.length === 0) {
    return '-';
  }

  return events
    .map((event, index) => {
      const lines = [
        `${index + 1}. ${event.occurredAt} — ${event.type}`,
        `   Route: ${event.route}`,
        `   Landing / Page: ${event.landingPage}`,
        `   Source: ${formatSourceForPrompt(event.source)}`,
      ];
      if (event.metadata) {
        lines.push(`   Metadata: ${JSON.stringify(event.metadata)}`);
      }
      return lines.join('\n');
    })
    .join('\n\n');
}

export function buildSessionAiPrompt(
  session: VisitorSessionListItem,
  events: VisitorSessionEvent[]
): string {
  const payload = buildSessionExportPayload(session, events);
  const summary = payload.session;

  return [
    'You are analyzing a TinyAll visitor session.',
    '',
    'TinyAll is a platform where businesses create online hubs for menus, stores, reservations, events, forms, informational content, and folders.',
    '',
    'Analyze this visitor session and tell me:',
    '1. What happened.',
    '2. What the visitor seemed interested in.',
    '3. Where the visitor may have dropped off.',
    '4. Whether the visitor showed buying/setup intent.',
    '5. Whether there are signs of confusion or friction.',
    '6. What TinyAll should improve in the funnel or product experience.',
    '7. What follow-up experiment or UX change we should consider.',
    '',
    '---',
    '',
    'Session summary',
    `- Session ID: ${summary.sessionId}`,
    `- Visitor ID: ${summary.visitorId}`,
    `- Identity: ${summary.identity}`,
    `- User ID: ${summary.userId}`,
    `- Created: ${summary.createdAt}`,
    `- Last activity: ${summary.lastActivityAt}`,
    `- Landing key: ${summary.landingKey}`,
    `- Landing path: ${summary.landingPath}`,
    `- Landing: ${summary.landing}`,
    `- Language: ${summary.language}`,
    `- Device: ${summary.device}`,
    `- Event count: ${summary.eventCount}`,
    `- Last event type: ${summary.lastEventType}`,
    '',
    'Source / UTM',
    formatSourceForPrompt(summary.source),
    '',
    'Event timeline',
    formatEventTimelineForPrompt(payload.events),
  ].join('\n');
}
